var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { isFirefox } from 'mobile-device-detect';
import createStore from 'redux-zero';
import configuration from '@assets/global_config';
import { appContextToTrackingData, appContextToTrackingDataPixel, extractPageUrl, getClientMeta, nowAndNowLocalToString, } from '@embed/helpers';
import { fetcher } from '@embed/store/fetchers';
import { parseExperiments, shouldRunExperiments, } from '@embed/store/tools/experiments';
import { ResetTransformEnum, } from '@embed/widgetTypes';
import { searchParams } from '@src/helpers';
import { getLastActivity, setLastActivity } from './session/guest.storage';
import { sendPixelTrackingWithSession, sendTrackingWithSession, } from './tools/tracking';
import { pruneNilValues } from './tools/utils';
import { actions as commonActions, initialState as commonInitialState, } from './common';
import { sanitizeWidgetParams } from './sanitizeWidgetParams';
import { urlWidgetParams } from './urlWidgetParams';
const { tracking } = configuration;
const { urls: trackingUrls, pixelUrls: pixelTrackingUrls } = tracking || {};
const initialState = Object.assign(Object.assign({}, commonInitialState), { params: {
        pwa_host: process.env.PWA_HOST || '',
        a11y_disable_focus: false,
        a11y_disable_previews: false,
        api_host: process.env.API_HOST || '',
        api_host_ads: process.env.API_HOST_ADS || '',
        api_host_tracking: process.env.PIXEL_HOST || '',
        api_host_tracking_legacy: process.env.PIXEL_NABOO_HOST || '',
        in_iframe: typeof window !== 'undefined' && window !== window.top,
        in_fwn_iframe: typeof window !== 'undefined' && window.name === 'fw-iframe',
        in_sdk_webview: typeof window !== 'undefined' &&
            ((_a = window.origin) === null || _a === void 0 ? void 0 : _a.includes('fw-sdk-webview')),
        loop: true,
        lsdesktop: true,
        autoplay: true,
        minimized: false,
        pip: !isFirefox,
        player_minimize: true,
        player_margin: undefined,
        player_placement: undefined,
        per_page: 10,
        reset_transform: ResetTransformEnum.FULLSCREEN,
        share: 'hash',
        pip_navigate: true,
        dir: '',
        ui_font_css: '',
        ui_primary_color: '',
        ui_button_color: '',
        ui_button_font_color: '',
        ui_border_style: 'rounded',
        ui_share_sheet: 'facebook,twitter,whatsapp,sms,copy,native',
    }, fetchedParams: null, session: {}, videos: {}, navigation: [], placeholders: [], loading: false, loaded: false, error: null, player: {
        isOpen: false,
        video: null,
    } });
export const storeFactory = ({ initialParams, // Params set by widget (embed feed, storyblock, etc.)
widgetParams, // Params set by publisher as widget attributes
 }) => {
    const { params } = initialState;
    const sanitizedUrlParams = sanitizeWidgetParams(urlWidgetParams(searchParams()));
    const sanitizedWidgetParams = sanitizeWidgetParams(widgetParams);
    const mergedParams = Object.assign(Object.assign(Object.assign(Object.assign({}, params), initialParams), sanitizedWidgetParams), sanitizedUrlParams);
    return createStore(Object.assign(Object.assign({}, initialState), { initialParams: initialParams, widgetParams: sanitizedWidgetParams, params: mergedParams, placeholders: [...new Array('per_page' in params ? params.per_page : 0)] }), []);
};
export const actions = () => (Object.assign(Object.assign({}, commonActions()), { fetchExperiments: (state, action) => __awaiter(void 0, void 0, void 0, function* () {
        const { params } = state;
        const { publisherClient } = action;
        const publisherClientId = publisherClient === null || publisherClient === void 0 ? void 0 : publisherClient.id;
        if (shouldRunExperiments(publisherClientId)) {
            const response = yield fetcher({
                url: '/embed/experiments',
                baseUrl: params.api_host,
            });
            const res = yield response.json();
            const { experiments, params: newParams } = parseExperiments(res.experiments, publisherClientId);
            return {
                experiments,
                experimentsFetched: true,
                params: Object.assign(Object.assign({}, params), newParams),
            };
        }
        return {
            experimentsFetched: true,
        };
    }), storeLastActivity: () => {
        if (!getLastActivity()) {
            setLastActivity();
        }
        return {};
    }, fetchedParams: (state, action) => {
        const { params, initialParams, widgetParams } = state;
        const { fetchedParams } = action;
        const cleanedFetchedParams = pruneNilValues(fetchedParams);
        const mergedParams = Object.assign(Object.assign(Object.assign(Object.assign({}, params), initialParams), cleanedFetchedParams), widgetParams);
        return {
            params: mergedParams,
            fetchedParams: cleanedFetchedParams,
        };
    }, updateParams: (state, action) => {
        const { params } = state;
        return {
            params: Object.assign(Object.assign({}, params), action),
        };
    }, setVideos: (state, action) => {
        return {
            videos: action.videos.reduce((map, video) => {
                map[video.encoded_id] = video;
                return map;
            }, {}),
            navigation: action.videos.map((video) => video.encoded_id),
            placeholders: [],
        };
    }, updateVideo: (state, action) => {
        const { video } = action;
        return {
            videos: Object.assign(Object.assign({}, state.videos), { [video.encoded_id]: Object.assign(Object.assign({}, state.videos[video.encoded_id]), { video }) }),
        };
    }, trackThumbnailImpression: (state, action) => {
        const { params, thumbnailImpressionIdsReportSuccess } = state;
        const { videos, appContext } = action;
        const eligibleForImpressionTracking = videos.filter(({ encoded_id }) => {
            return !thumbnailImpressionIdsReportSuccess[encoded_id];
        });
        if (eligibleForImpressionTracking.length === 0) {
            return state;
        }
        const [now, nowLocal] = nowAndNowLocalToString();
        // Send Naboo tracking
        const data = Object.assign({ displayed_at: now, displayed_at_local: nowLocal, page_url: extractPageUrl({ params }), variant: eligibleForImpressionTracking[0], video_ids: eligibleForImpressionTracking.map((video) => video.encoded_id) }, appContextToTrackingData({ appContext }));
        sendTrackingWithSession({
            url: trackingUrls({}).thumbnailImpressions,
            data,
            host: params.api_host_tracking_legacy,
        });
        // Send Pixel tracking
        const pixelContext = appContextToTrackingDataPixel({ appContext });
        const pixelData = {
            event_properties: pruneNilValues(Object.assign({ videos: eligibleForImpressionTracking.map((video) => ({
                    _video_id: video.encoded_id,
                    variant: video.variant,
                })) }, pixelContext)),
            _publisher_client_id: pixelContext._publisher_client_id,
            publisher_client_source: extractPageUrl({ params }),
        };
        sendPixelTrackingWithSession({
            url: pixelTrackingUrls().feedCreateThumbnailImpression,
            data: pixelData,
            host: params.api_host_tracking,
        });
        return {
            thumbnailImpressionIdsReportSuccess: Object.assign(Object.assign({}, thumbnailImpressionIdsReportSuccess), eligibleForImpressionTracking.reduce((map, video) => {
                map[video.encoded_id] = true;
                return map;
            }, Object.create(null))),
        };
    }, showVideoPlayer: (state, action) => {
        const { autoplayIds } = state;
        const { video, playlist, muted } = action;
        return {
            player: { video, playlist, muted, isOpen: true },
            autoplayIds: {},
            autoplayIdsPrevious: Object.assign({}, autoplayIds),
        };
    }, hideVideoPlayer: (state) => {
        const { autoplayIdsPrevious, player } = state;
        return {
            player: Object.assign(Object.assign({}, player), { isOpen: false }),
            autoplayIds: Object.assign({}, autoplayIdsPrevious),
            autoplayIdsPrevious: {},
        };
    }, trackEmbedInstance: (state, action) => {
        const { appContext } = action;
        const { params } = appContext;
        const { mode, page_type, placement } = params;
        const data = pruneNilValues(Object.assign(Object.assign(Object.assign({}, getClientMeta()), { mode: mode, page_type: page_type, page_url: extractPageUrl({ params }), placement: placement || '' }), appContextToTrackingData({ appContext })));
        sendTrackingWithSession({
            url: '/embed/instances',
            data,
            host: params.api_host_tracking_legacy,
        });
        const contextDataPixel = appContextToTrackingDataPixel({ appContext });
        const pixelData = {
            event_properties: pruneNilValues(Object.assign({ autoplay: params.autoplay, placement }, contextDataPixel)),
            _publisher_client_id: contextDataPixel._publisher_client_id,
            publisher_client_source: extractPageUrl({ params }),
        };
        sendPixelTrackingWithSession({
            url: pixelTrackingUrls().feedCreateEmbedInstance,
            data: pixelData,
            host: params.api_host_tracking,
        });
        return {};
    } }));
